import { React, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Autocomplete,
  Typography,
  Button,
  Link,
  IconButton,
  Alert,
  Radio,
  RadioGroup,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Http from "../../../services/Http";
import dataService from "../../../services/data.service";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Chip from "@mui/material/Chip";
import useToast from "../../../hooks/Toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import InitializePlanConfirmationModal from "./InitializePlanConfirmation";
import ImportPlanCopyValidationModal from "./ImportPlanCopyValidationModal";
import UploadDownloadActualsDialog from "./UploadDownloadActualsDialog";
import Block from "@mui/icons-material/Block";
import LoadingScreen from "../LoadingScreen";
import { useHMG } from "../../../HMGContext";
import MultiSelectDropdown from "../../dynamic-mutliselect/MultiSelectDropdown";
import { IoMdSync } from "react-icons/io";
import { useSocketContext } from "../../../contexts/SocketProvider";
import { subscribeToJobProgress, unsubscribeFromJobProgress } from "../../../services/socketService";
import { useJobProgress } from "../../../contexts/JobProgressContext";
import dialogDataPrefetchService from "../../../services/dialogDataPrefetch.service";
import {
  planDepartmentOptionsFromCache,
  departmentRowDisplayName,
  driverConfigDepartmentPayloadFromOptions,
} from "../../../Utils/planDepartmentDropdownOptions";

// Months reused from settings page shape: { label, value } for Jan..Dec
const MONTH_OPTIONS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];


// const getSyncDotColor = (lastSyncedAt) => {
//   if (!lastSyncedAt) return "#22C55E";
//   const ms = typeof lastSyncedAt === "number" ? lastSyncedAt : new Date(lastSyncedAt).getTime();
//   if (Number.isNaN(ms)) return "#22C55E";
//   const days = (Date.now() - ms) / (1000 * 60 * 60 * 24);
//   return days > 30 ? "#FFAB00" : "#22C55E";
// };

/** Reads optional manual-actuals upload audit fields from org closed-month / actuals payload (names may vary by API version). */
function pickManualActualsUploadMeta(data) {
  if (data == null || typeof data !== "object") {
    return { uploadedAt: null, uploadedBy: null };
  }
  const uploadedAt =
    data.lastManualActualsUploadedAt ??
    data.manualActualsLastUploadedAt ??
    data.lastManualUploadAt ??
    data.manualActualsUploadedAt ??
    data.lastUploadedManualActualsAt ??
    null;
  const uploadedBy =
    data.lastManualActualsUploadedBy ??
    data.manualActualsUploadedBy ??
    data.manualActualsUploadedUserName ??
    data.uploadedByUserName ??
    data.lastManualUploadUserName ??
    null;
  const byStr =
    uploadedBy != null && String(uploadedBy).trim() !== "" ? String(uploadedBy).trim() : null;
  return { uploadedAt, uploadedBy: byStr };
}

const isPlanPublished = (plan) =>
  (plan?.planStatus || plan?.planStatusName || "")
    .toString()
    .toLowerCase() === "published";

const publishedChipSx = {
  color: "#118D57",
  backgroundColor: "#22C55E29",
  fontWeight: 700,
  borderRadius: "6px",
  height: 22,
};

const autocompletePopperSlotProps = {
  popper: {
    sx: {
      "& .MuiAutocomplete-listbox": {
        "& .MuiAutocomplete-option": {
          padding: "6px 16px !important",
        },
      },
    },
  },
  paper: {
    sx: { maxHeight: 220 },
  },
};

const autocompleteInputSx = {
  ".MuiInputLabel-root": { fontSize: "14px" },
  ".MuiOutlinedInput-root": { fontSize: "14px" },
};

const PlanOptionLabel = ({ plan }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
    <span>{plan.versionName}</span>
    {isPlanPublished(plan) && (
      <Chip size="small" label="Published" sx={publishedChipSx} />
    )}
  </Box>
);

const ImportActualModal = ({ 
  isOpen, 
  setOpenImportActualModal, 
  isSingle, 
  rowsData, 
  setLoading, 
  showToast = () => {}, 
  onClose: onCloseCallback,
  onJobComplete,
  preselectedDepartmentId,
  preselectedDepartmentMappingId,
  preselectedDepartmentName,
  preselectedStructureCodeId,
  /** Actuals only: functional code id for GL dropdown (`ccmsFunctionalCodeId` / `functionalCodeId`); plan row `coaId`. */
  preselectedFunctionalCodeId,
  mode = "plan",
}) => {
  const { Toast } = useToast();
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [accountsOptions, setAccountsOptions] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [selectedPlanType, setSelectedPlanType] = useState("");
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");
  const [planOptions, setPlanOptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [copyOnlyValue, setCopyOnlyValue] = useState(false);
  // TPX-2219: copy only the drivers (rates zeroed), no values. Mutually exclusive with copyOnlyValue.
  const [copyDriversOnly, setCopyDriversOnly] = useState(false);
  const [selectedDepartmentNames, setSelectedDepartmentNames] = useState([]);
  const [selectedAccountsNames, setSelectedAccountsNames] = useState([]);
  const [openInitializePlanConfirmationModal, setOpenInitializePlanConfirmationModal] = useState(false);
  const [copyValidationOpen, setCopyValidationOpen] = useState(false);
  const [copyValidationResult, setCopyValidationResult] = useState(null);
  const [validatingCopy, setValidatingCopy] = useState(false);
  const [copyExcludedCoaIds, setCopyExcludedCoaIds] = useState([]);
  const [copyIncludedCoaIds, setCopyIncludedCoaIds] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [updatedCoaVersions, setUpdatedCoaVersions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { stomp } = useSocketContext();
  const {addJob, updateJob} = useJobProgress();

  // Actuals-mode-only state
  const [actualsSource, setActualsSource] = useState(null); // 1 = Live integration, 2 = Manual upload
  const [selectedSource, setSelectedSource] = useState("LIVE"); // "LIVE" | "TEMPLATE"
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedGlCodes, setSelectedGlCodes] = useState([]);
  const [glCodeOptions, setGlCodeOptions] = useState([]);
  const [templateUploadOpen, setTemplateUploadOpen] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  /** Calendar year for Copy data from actuals API (`year` on request body). */
  const [actualsCopyYear, setActualsCopyYear] = useState(() => dayjs().year());
  /** Last manual Actuals file on server (from org get-data); tooltip shows uploader when API sends it. */
  const [manualActualsLastUploadedAt, setManualActualsLastUploadedAt] = useState(null);
  const [manualActualsLastUploadedBy, setManualActualsLastUploadedBy] = useState(null);
  /** Copy window basis: "YEAR" (year + months) or "CLOSED_MONTH" (derived from configured closed month). */
  const [copyBasis, setCopyBasis] = useState("YEAR");
  /** Configured effective closed accounting month (YYYYMM) for the org/property; null when not configured. */
  const [closedAccountingMonth, setClosedAccountingMonth] = useState(null);

  const liveIntegrationEnabled = actualsSource === 1;
  const closedMonthLabel = closedAccountingMonth
    ? dayjs().year(Math.floor(closedAccountingMonth / 100)).month((closedAccountingMonth % 100) - 1).format("MMMM YYYY")
    : null;
  // const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const propertyOptions = dataService.getPropertiesForHMG();
  const planTypeOptions = dataService.getPlanTypeOptions();
  let organizationId = dataService.getSelectedHMGId();

  const {setCurrentModalName, setSelectedHMG, setIsReprocessingActive, isReprocessingActive} = useHMG();

  // True while the selected HMG has an active reprocessing job (server flag at page load, or a live
  // align/sync trigger this session). While active, copying/aligning is blocked to avoid queueing
  // against data that is mid-reprocess.
  const isHmgReprocessing = Boolean(dataService.getSelectedHMG()?.isReprocessing) || isReprocessingActive;

  const handleClose = () => {
    if (isImporting) return;  
    setOpenImportActualModal(false);
    setSelectedProperty("");
    setSelectedPlanType("");
    setCopyOnlyValue(false);
    setSelectedFiscalYear("");
    setSelectedDepartmentNames([]);
    setSelectedAccountsNames([]);
    setSelectedPlan("");  // Clear selectedPlan 
    setUpdatedCoaVersions({});
    setSelectedSource("LIVE");
    setSelectedMonths([]);
    setSelectedGlCodes([]);
    setTemplateFile(null);
    setActualsSource(null);
    setActualsCopyYear(dayjs().year());
    setManualActualsLastUploadedAt(null);
    setManualActualsLastUploadedBy(null);
    setCopyBasis("YEAR");
    setClosedAccountingMonth(null);
    formik.resetForm(); // Reset formik values
    setCurrentModalName(null); // Reset current modal name
    if (onCloseCallback && typeof onCloseCallback === 'function') {
      onCloseCallback();
    }
  };

  useEffect(() => {
    if (isOpen) {
      getAllDepartmentTypesData();
      getAllAccountStructureCodeData();
      if (rowsData && rowsData.length > 0) {
        const initialCoaVersions = {};
        rowsData.filter(row => row != null).forEach(row => {
          if (row.id && row.coaVersion) {
            initialCoaVersions[row.id] = row.coaVersion;
          }
        });
        setUpdatedCoaVersions(initialCoaVersions);
      }
    }
  }, [isOpen, rowsData]); // Dependency array includes `isOpen` and `rowsData`

  // Actuals mode: fetch the org's actualsSource (1 = Live integration, 2 = Manual upload)
  // so we can enable/disable the Live integration radio.
  useEffect(() => {
    if (!isOpen || mode !== "actuals" || !organizationId) return;
    // One plan = one property; resolve the closed month against that property (override → org fallback).
    const propertyId = rowsData?.[0]?.propertyIds?.[0] ?? rowsData?.[0]?.propertyId ?? null;
    dataService.getClosedMonthAccountData(organizationId).then((data) => {
      if (data != null) {
        const src = data?.actualsSource ?? null;
        setActualsSource(src);
        // When the property is unknown, fall back to the org-level closed month from this response.
        if (!propertyId) {
          setClosedAccountingMonth(data?.closedAccountingMonth ?? null);
        }
        const manualMeta = pickManualActualsUploadMeta(data);
        setManualActualsLastUploadedAt(manualMeta.uploadedAt);
        setManualActualsLastUploadedBy(manualMeta.uploadedBy);
        // Default: Live when available, else manual uploaded Actuals
        // setLastSyncedAt(data?.lastSyncedAt ?? null);
        setSelectedSource(src === 1 ? "LIVE" : "TEMPLATE");
      }
    });
    if (propertyId) {
      dataService.getResolvedClosedMonth(organizationId, propertyId).then((data) => {
        if (data != null) {
          setClosedAccountingMonth(data?.closedAccountingMonth ?? null);
        }
      });
    }
  }, [isOpen, mode, organizationId, rowsData?.[0]?.propertyIds?.[0], rowsData?.[0]?.propertyId]);

  // Load GL codes (functional codes) for the GL code(s) dropdown.
  // Actuals mode: scoped to the first selected target plan.
  // Plan copy mode: scoped to the chosen source plan; falls back to org-level list.
  useEffect(() => {
    if (!isOpen) return;
    let planIdForScope;
    if (mode === "actuals") {
      const validRows = (rowsData || []).filter((r) => r != null);
      planIdForScope = validRows[0]?.id;
    } else {
      planIdForScope = selectedPlan?.id;
    }
    const url = planIdForScope
      ? `chart-of-accounts/functional-codes-with-department/${planIdForScope}`
      : organizationId
      ? `/chart-of-accounts/functional-codes/${organizationId}`
      : null;
    if (!url) return;
    Http.GET(url).then((resp) => {
      if (resp?.status === 200 && Array.isArray(resp.data)) {
        const map = new Map();
        resp.data.forEach((fc) => {
          const id = fc.ccmsFunctionalCodeId || fc.functionalCodeId;
          if (!map.has(id)) {
            map.set(id, {
              id,
              functionalCode: fc.functionalCode,
              functionalCodeName: fc.functionalCodeName,
              displayName: `${fc.functionalCode} - ${fc.functionalCodeName}`,
            });
          }
        });
        setGlCodeOptions(Array.from(map.values()));
      }
    });
  }, [isOpen, mode, rowsData, organizationId, selectedPlan?.id]);

  // Preselect GL code from the originating line when ActionCell passes preselectedFunctionalCodeId.
  // Applies to both actuals and plan-copy modes since both now use the GL code(s) dropdown.
  useEffect(() => {
    if (!isOpen) return;
    if (glCodeOptions.length === 0 || selectedGlCodes.length > 0) return;
    const raw =
      preselectedFunctionalCodeId != null && preselectedFunctionalCodeId !== ""
        ? String(preselectedFunctionalCodeId).trim()
        : "";
    if (raw === "") return;

    const preNum = Number(raw);
    const hasPreNum = Number.isFinite(preNum);
    const match = glCodeOptions.find((g) => {
      if (String(g.id) === raw) return true;
      if (hasPreNum && Number.isFinite(Number(g.id)) && Number(g.id) === preNum) return true;
      return false;
    });
    if (match) setSelectedGlCodes([match]);
  }, [isOpen, glCodeOptions, preselectedFunctionalCodeId, selectedGlCodes.length]);

  // Disable Copy until year + months + GL codes are set; LIVE still requires org live integration.
  // Manual uploaded (TEMPLATE): file is optional — actuals may already be in DB from a separate upload.
  const isActualsFormValid = () => {
    if (copyBasis === "CLOSED_MONTH") {
      if (closedAccountingMonth == null) return false;
    } else {
      if (actualsCopyYear == null || !Number.isFinite(Number(actualsCopyYear))) return false;
      if (selectedMonths.length === 0) return false;
    }
    if (selectedGlCodes.length === 0) return false;
    if (selectedSource === "LIVE") return liveIntegrationEnabled;
    if (selectedSource === "TEMPLATE") return true;
    return false;
  };

  // True when every available GL code is selected. Drives the "Copy all line items"
  // checkbox (two-way synced with the GL dropdown) and tells the backend to skip
  // dependent-line reprocessing since all lines are imported.
  const isCopyAllLineItems = () =>
    glCodeOptions.length > 0 && selectedGlCodes.length === glCodeOptions.length;

  // Auto-select property when only one option is available
  useEffect(() => {
    if (isOpen && propertyOptions?.length === 1 && !selectedProperty) {
      const singleValue = propertyOptions[0].value;
      setSelectedProperty(singleValue);
      formik.setFieldValue("propertyId", singleValue);
    }
  }, [isOpen, propertyOptions]);

  useEffect(() => {
    if (isOpen && departmentOptions.length > 0 && accountsOptions.length > 0) {
      if (selectedDepartmentNames.length === 0) {
        let preselectedDept = null;
        const hasMapping =
          preselectedDepartmentMappingId != null &&
          preselectedDepartmentMappingId !== "";
        if (hasMapping) {
          const mid = Number(preselectedDepartmentMappingId);
          preselectedDept = departmentOptions.find(
            (dept) =>
              dept.optionKind === "sub" &&
              dept.departmentMappingId != null &&
              Number(dept.departmentMappingId) === mid
          );
        }
        if (!preselectedDept && preselectedDepartmentId) {
          preselectedDept = departmentOptions.find(
            (dept) =>
              dept.id === preselectedDepartmentId ||
              (dept.optionKind === "dept" &&
                Number(dept.departmentId) === Number(preselectedDepartmentId))
          );
        } else if (!preselectedDept && preselectedDepartmentName) {
          const want = preselectedDepartmentName.toLowerCase();
          preselectedDept = departmentOptions.find((dept) => {
            const label = departmentRowDisplayName(dept);
            return (
              dept.departmentName?.toLowerCase() === want ||
              label?.toLowerCase() === want
            );
          });
        }
        
        if (preselectedDept) {
          setSelectedDepartmentNames([preselectedDept]);
        }
      }

      if (preselectedStructureCodeId && selectedAccountsNames.length === 0) {
        const preselectedAccount = accountsOptions.find(acc => acc.id === preselectedStructureCodeId);
        if (preselectedAccount) {
          setSelectedAccountsNames([preselectedAccount]);
        }
      }
    }
  }, [isOpen, departmentOptions.length, accountsOptions.length, preselectedDepartmentId, preselectedDepartmentMappingId, preselectedDepartmentName, preselectedStructureCodeId]);


  function getAllDepartmentTypesData() {
    const firstPlan = (rowsData || []).find((row) => row != null);
    const planId =
      firstPlan?.id ?? firstPlan?.planId ?? firstPlan?.planDetailsId ?? null;
    const hmgId = organizationId || firstPlan?.organizationId || dataService.getSelectedHMGId();

    const loadFlatDepartments = () => {
      Http.GET("plan-details/get-all-departments?organizationId=" + hmgId).then((res) => {
        if (res && res.status == 200) {
          setDepartmentOptions(planDepartmentOptionsFromCache({ departments: res.data }));
        }
      });
    };

    if (planId != null && planId !== "" && hmgId) {
      dialogDataPrefetchService
        .ensureDepartmentPlanRows(planId, hmgId)
        .then((rows) => {
          if (rows && rows.length > 0) {
            setDepartmentOptions(
              planDepartmentOptionsFromCache({ departmentPlanRows: rows })
            );
          } else {
            loadFlatDepartments();
          }
        })
        .catch(() => loadFlatDepartments());
    } else {
      loadFlatDepartments();
    }
  }

  const fetchVersions = async (property, planType, fiscalYear) => {

    formik.setFieldValue("selectedPlan", null);
    setSelectedPlan("");
    setIsLoadingPlans(true);
    try {

      const queryParams = new URLSearchParams();
      queryParams.append("organizationId", organizationId);
      // Add parameters only if they have values
      if (property) queryParams.append("propertyId", property);
      if (fiscalYear) queryParams.append("planYear", fiscalYear);
      if (planType) queryParams.append("planType", planType);
      const selectedPlanIds = rowsData?.filter(row => row != null).map(row => row?.id);
      if (selectedPlanIds && selectedPlanIds?.length > 0) {
        queryParams.append("importingPlansIds", selectedPlanIds.join(","));
      }
      const res = await Http.GET(`plan/get-plans?${queryParams}`);

      if (res && res.status === 200) {
        setPlanOptions(res.data.filter(row => 
          !(rowsData || []).filter(item => item != null).some(item => item.id === row.id) // Remove if ID exists in rowsData
        ));
      } else {
        console.error("Error fetching versions");
      }
    } catch (error) {
      console.error("API error:", error);
    }
    finally {
      setIsLoadingPlans(false); // Stop loading
    }
  };

  function getAllAccountStructureCodeData() {
    const savedHmgId = localStorage.getItem('selectedHmgId');
    Http.GET("chart-of-accounts/all-structure-code?hmgId="+savedHmgId).then((res) => {
      if (res && res.status == 200) {
        const dropdownOptions = res.data.map((m) => ({
          id: m.ccmsStructureCodeId, // ID of the structure
          structureCodeName: m.structureCodeName, // Replace with the appropriate field for value
        }));

        setAccountsOptions(dropdownOptions);
      }
    });
  }

  const validationSchema = Yup.object().shape({
    selectedPlan: Yup.object()
      .shape({
        id: Yup.string().required("This field is required"),
      }).nullable().required("This field is required"),
      propertyId: Yup.string().required("This field is required"),
  });
  

  const formik = useFormik({
    initialValues: { 
      selectedPlan: null,
      propertyId: null
     },  // Default to null
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values) => handleImport(values),
  });

  const sortedPlanOptions = useMemo(
    () => [...planOptions].sort((a, b) => a.versionName.localeCompare(b.versionName)),
    [planOptions]
  );

  const buildPlanImportPayload = (excludedCoaIds = [], includedCoaIds = []) => {
    const validRowsData = (rowsData || []).filter((row) => row != null);
    const deptPayload = driverConfigDepartmentPayloadFromOptions(selectedDepartmentNames);
    return {
      organizationId: organizationId,
      exportingActualPlanId: selectedPlan?.id,
      planNames: validRowsData.map((row) => row.versionName),
      importingActualPlanIds: validRowsData
        // TPX-2219: a drivers-only copy works across COA versions, so a ccmsTemplateId mismatch (which a newer
        // COA version causes) no longer excludes the target — the backend matches driver assignments per COA id.
        .filter((row) => copyDriversOnly || row.ccmsTemplateId === selectedPlan.ccmsTemplateId)
        .map((row) => row.id),
      ...(deptPayload.departmentIds?.length > 0 && { departmentIds: deptPayload.departmentIds }),
      ...(deptPayload.departmentMappingIds?.length > 0 && {
        departmentMappingIds: deptPayload.departmentMappingIds,
      }),
      // accountIds carries functional code ids (= plan row coaId), not structure code ids.
      ...(selectedGlCodes?.length > 0 && {
        accountIds: selectedGlCodes.map((g) => g.id),
      }),
      excludedCoaIds,
      includedCoaIds,
      // Month-by-month copy: optional here — sending no months copies the whole year.
      ...(selectedMonths?.length > 0 && {
        months: selectedMonths.map((m) => m.value),
      }),
      copyOnlyValue: copyOnlyValue,
      copyDriversOnly: copyDriversOnly,
      copyAllLineItems: isCopyAllLineItems(),
      createDraft: false,
      userId: dataService.getUserId(),
      userName: dataService.getFullUserName(),
    };
  };

  // One month picker shared by the actuals and plan copy flows.
  const renderMonthSelector = (label, disabled = false) => (
    <MultiSelectDropdown
      label={label}
      placeholder=""
      options={[{ value: "select-all", label: "Select All" }, ...MONTH_OPTIONS]}
      value={selectedMonths}
      getOptionLabel={(option) => option.label}
      getOptionId={(option) => option.value}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      disabled={disabled}
      onChange={(event, clickedOption) => {
        if (clickedOption.value === "select-all") {
          setSelectedMonths(
            selectedMonths.length === MONTH_OPTIONS.length ? [] : MONTH_OPTIONS
          );
          return;
        }
        const isSelected = selectedMonths.some((m) => m.value === clickedOption.value);
        setSelectedMonths(
          isSelected
            ? selectedMonths.filter((m) => m.value !== clickedOption.value)
            : [...selectedMonths, clickedOption]
        );
      }}
      searchPlaceholder="Search months..."
    />
  );

  const handleImportClick = async () => {
    if (mode === "actuals") {
      if (!isActualsFormValid()) return;
      handleImportConformation();
      setCurrentModalName(null);
      return;
    }
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({ selectedPlan: true, propertyId: true});
      return;
    }
    setValidatingCopy(true);
    setCopyValidationResult(null);
    try {
      const validationPayload = buildPlanImportPayload();
      const res = await Http.POST(
        "plan-details/import-actuals/validate?type=PLAN",
        validationPayload
      );
      const result = res?.data ?? res;
      if (result?.hasWarnings) {
        setCopyValidationResult(result);
        setCopyValidationOpen(true);
        setCurrentModalName("Copy data from plan — review differences");
        return;
      }
      handleImportConformation();
      setCurrentModalName(null);
    } catch (err) {
      console.error("Copy validation failed:", err);
      showToast(
        "Could not validate line items before copy. Please try again.",
        "error",
        null,
        6000
      );
    } finally {
      setValidatingCopy(false);
    }
  };

  const handleCopyValidationClose = () => {
    setCopyValidationOpen(false);
    setCopyValidationResult(null);
    setCopyExcludedCoaIds([]);
    setCopyIncludedCoaIds([]);
    setCurrentModalName(mode === "actuals" ? "Copy data from actuals" : "Copy data from plan");
  };

  const handleCopyValidationContinue = (excludedCoaIds = [], includedCoaIds = []) => {
    setCopyExcludedCoaIds(excludedCoaIds);
    setCopyIncludedCoaIds(includedCoaIds);
    setCopyValidationOpen(false);
    setCopyValidationResult(null);
    handleImportConformation();
    setCurrentModalName(null);
  };

  function handleImportConformation (){
    setOpenInitializePlanConfirmationModal(true);
    setCurrentModalName(mode === "actuals" ? "Copy data from actuals" : "Copy data from plan")
  }

  const handelinitializePlanModalClose = () => {
    setOpenInitializePlanConfirmationModal(false);
    setCurrentModalName(null)
  }
  
  const subscribeToJob = (jobId, planName) => {
    if (!stomp) { console.warn('[subscribeToJob] stomp is null'); return; }
    const targetPlanIds = (rowsData || []).filter((r) => r != null).map((r) => r.id).filter(Boolean);
    addJob(jobId, planName, targetPlanIds);
    subscribeToJobProgress(jobId, (msg) => {
      const data = JSON.parse(msg.body);
      updateJob(jobId, data, targetPlanIds);
      if (data.status === "COMPLETED" || parseInt(data.completedPercentage, 10) === 100) {
        unsubscribeFromJobProgress(jobId);
        if (onJobComplete) {
          onJobComplete();
        }
      }
    });
  };

  function handleImport() {
    // setLoading(true);
    setIsImporting(true);
    setOpenInitializePlanConfirmationModal(false);
    if (!stomp || !stomp.connected) {
      showToast("Connection is not ready for live progress. Please wait a moment and try again.", "warning", null, 6000);
      setIsImporting(false);
      return;
    }

    const validRowsData = (rowsData || []).filter((row) => row != null);
    const jobId = crypto.randomUUID();
    const planName = validRowsData[0]?.versionName || "Unknown";
    subscribeToJob(jobId, planName);

    // Confirm the copy has started. From All Plans the progress bar isn't visible (not on the
    // plan), and even on the Plan Details page this gives an immediate acknowledgement that the
    // job kicked off, pointing the user to the Plan Details page to watch progress.
    {
      const sourceLabel = mode === "actuals" ? "actuals" : "plan";
      const target = validRowsData.length > 1
        ? `${validRowsData.length} plans`
        : `plan "${planName}"`;
      showToast(
        `Copy from ${sourceLabel} started for ${target}. To track progress, open the plan and check the Plan Details page.`,
        "success",
        null,
        8000
      );
    }

    const payLoad = mode === "actuals"
      ? {
          jobId,
          organizationId: organizationId,
          planNames: validRowsData.map((row) => row.versionName),
          importingActualPlanIds: validRowsData.map((row) => row.id),
          basis: copyBasis,
          source: selectedSource,
          glCodeIds: selectedGlCodes.map((g) => g.id),
          copyAllLineItems: isCopyAllLineItems(),
          createDraft: false,
          // Year mode sends the selected year + months; closed-month mode lets the backend derive them.
          ...(copyBasis === "YEAR"
            ? { year: Number(actualsCopyYear), months: selectedMonths.map((m) => m.value) }
            : {}),
          ...(selectedSource === "TEMPLATE" && templateFile ? { templateFileName: templateFile.name } : {}),
          userId: dataService.getUserId(),
          userName: dataService.getFullUserName(),
        }
      : {
          jobId,
          ...buildPlanImportPayload(copyExcludedCoaIds, copyIncludedCoaIds),
        };
    // handleClose();
    Http.POST(`plan-details/import-actuals?type=${mode === "actuals" ? "ACTUAL" : "PLAN"}`, payLoad).then((res) => {
      const serverJobId = res?.data;
      if (serverJobId && serverJobId !== jobId) {
        console.warn("[import-actuals] jobId mismatch: client", jobId, "server", serverJobId);
      }
    })
    .finally(() => {
      setIsImporting(false);
      setCopyExcludedCoaIds([]);
      setCopyIncludedCoaIds([]);
      handleClose();
      setCurrentModalName(null);
    });

  }

  const getCompatiblePlansCount = () => {
    const validRowsData = (rowsData || []).filter(row => row != null);
    if (!selectedPlan || selectedPlan === "") {
      return validRowsData.length; // If no plan selected, all are considered compatible
    }
    
    const compatibleCount = validRowsData.filter(row => 
      row.ccmsTemplateId === selectedPlan.ccmsTemplateId
    ).length;
    
    return compatibleCount;
  };

  const areAllPlansIncompatible = () => {
    const validRowsData = (rowsData || []).filter(row => row != null);
    if (!selectedPlan || selectedPlan === "" || validRowsData.length === 0) {
      return false; // If no plan selected or no data, don't disable
    }
    
    // Check if ALL rows are incompatible (none have matching ccmsTemplateId)
    return validRowsData.every(row => row.ccmsTemplateId !== selectedPlan.ccmsTemplateId);
  };
  
  const hasIncompatiblePlans = () => {
    const validRowsData = (rowsData || []).filter(row => row != null);
    if (!selectedPlan || selectedPlan === "") {
      return false;
    }
    
    return validRowsData.some(row => row.ccmsTemplateId !== selectedPlan.ccmsTemplateId);
  };
  
  const shouldShowChip = () => {
    const validRowsData = (rowsData || []).filter(row => row != null);
    if (!selectedPlan || selectedPlan === "" || validRowsData.length <= 1) {
      return false;
    }
    
    return hasIncompatiblePlans() || validRowsData.every(row => row.ccmsTemplateId === selectedPlan.ccmsTemplateId);
};

  const getChipStyles = () => {
    const compatibleCount = getCompatiblePlansCount();
    const validRowsData = (rowsData || []).filter(row => row != null);
    const totalCount = validRowsData.length;
    
    if (compatibleCount === totalCount) {
      return {
        backgroundColor: "#22C55E29",
        color: "#118D57",
      };
    } else if (compatibleCount === 0) {
      return {
        backgroundColor: "#FF563029", 
        color: "#B71D18",
      };
    } else {
      return {
        backgroundColor: "#FFAB0029", 
        color: "#B76E00",
      };
    }
  };

  const alignCOAVersion = ({organizationId, templateId, row, selectedPlan}) => {
    const currentRowCoaVersion = updatedCoaVersions[row.id] || row.coaVersionId;
    
    const alignPlanCoaversion = {
      organizationId: organizationId,
      templateId: templateId,
      basePlanCOAVersionId: selectedPlan.coaVersion,
      targetPlanCOAVersionId: currentRowCoaVersion,
      targetPlanId: row.id,
    }
    setIsLoading(true);

    Http.POST("chart-of-accounts/align-plan-coaversion", alignPlanCoaversion).then((res) => {
      if (res && res.status == 200) {
        // Align now runs asynchronously: the queue rows are saved and reprocessing runs in the
        // background. Mark the HMG as reprocessing so the app goes read-only and the global
        // HMGReprocessingLoader shows live progress (mirrors SyncLatestMappingDialog). That loader
        // reloads the window once reprocessing completes.
        const cachedHmg = dataService.getSelectedHMG();
        if (cachedHmg) {
          cachedHmg.isReprocessing = true;
        }
        setSelectedHMG((prev) => (prev ? { ...prev, isReprocessing: true } : prev));
        setIsReprocessingActive(true);
        showToast(`Aligning COA version for plan ${row.versionName}. Reprocessing in progress…`, "info");
        setIsLoading(false);
        handleClose();
      } else {
        setIsLoading(false);
      }
    }).catch(() => {
      setIsLoading(false);
    });

  }

  return (
    <Dialog
      open={isOpen}
      // onClose={handleClose}
      aria-labelledby="import-dialog-title"
      PaperProps={{
        sx: {
          width: 600,
          overflowX: "hidden",
          borderRadius: 2,
          p: 2,
          bgcolor: "#FFFFFF",
        },
      }}>
      <Toast />

      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <DialogTitle
            id="import-dialog-title"
            variant="h4"
            sx={{
              p: 0,
              fontSize: "24px",
              lineHeight: "36px",
              fontWeight: 700,
              color: "#212B36",
            }}>
            {mode === "actuals" ? "Copy data from actuals" : "Copy data from plan"}
          </DialogTitle>
          <IconButton
            onClick={handleClose}
            sx={{ p: 1 }}>
            <ClearIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        </Box>
        <Typography
          sx={{ color: "#637381", fontSize: "12px", fontWeight: 400 }}>
          *Required fields
        </Typography>
      </Box>

      <DialogContent className="!overflow-x-hidden" sx={{ p: 0, width: "568px" }}>

        {/* {isSingle ? (
          <div></div>
        ) : ( */}
        {isImporting ? 
        <div className="min-h-[350px] flex justify-center items-center">
          <LoadingScreen/>
        </div>
        :
        <>
          <Box sx={{ width: 568, mt: 2 }}>
          {isHmgReprocessing && (
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon className="mt-1" sx={{ color: "#00B8D9" }} />}
              sx={{
                color: "#006C9C",
                border: "none",
                minHeight: "50px",
                backgroundColor: "#CAFDF5",
                fontSize: "14px",
                lineHeight: "22px",
                display: "flex",
                padding: "0 16px",
                marginBottom: "16px",
                marginTop: "16px",
              }}
            >
              Reprocessing is in progress for this hotel. Copying is disabled until it completes.
            </Alert>
          )}
          {mode === "plan" && (
          <Alert
                            varvariant="standard"
                            severity="warning"
                            // onClose={() => { }}
                            icon={<ErrorIcon className="mt-1"  sx={{ color: '#FFAB00' }} />}
                            sx={{
                                color: '#7A4100',
                                borderColor: '#FFF5CC',
                                border: 'none',
                                minHeight: '50px',
                                backgroundColor: '#FFF5CC',
                                fontSize: '14px',
                                lineHeight: '22px',
                                display: 'flex',
                                // alignItems: 'center',
                                padding: '0 16px',
                                marginBottom: '16px',
                                marginTop: "16px",
                                '& .MuiAlert-icon': {
                                    color: '#8E33FF',
                                },
                                '& .MuiAlert-message': {
                                    // width: '100%',  // Ensures the message takes the full width
                                },
                            }}
                        >
                          <ul className="list-disc pl-5">
                            <li>Only compatible plan(s) will be processed.  </li>
                            <li>
                                 All departments and rows will update, the driver type will change to ‘Monthly’, and adjustments will be set to zero.
                            </li>
                            <li>This action cannot be undone.</li>
                          </ul>
                          </Alert>
          )}
                          <div className="flex items-center gap-2"> 
                            <Typography
                              sx={{ color: "#212A36", fontSize: "14px", fontWeight: 600 }}>
                              Copy to
                            </Typography>
                            {/* <Box
                              sx={{
                                "& .MuiChip-root": {
                                  backgroundColor: "#919EAB29",
                                  color: "#637381",
                                  borderRadius: "6px",
                                  fontWeight: "700"
                                },
                              }}
                            >
                              <Chip size="small" label={""} />
                            </Box>   */}
                            {shouldShowChip() && (
                                <Box
                                  sx={{
                                    "& .MuiChip-root": {
                                      ...getChipStyles(),
                                      borderRadius: "6px",
                                      fontWeight: "700"
                                    },
                                  }}
                                >
                                  <Chip 
                                    size="small" 
                                    label={`${getCompatiblePlansCount()} of ${(rowsData || []).filter(row => row != null).length} plans are compatible`} 
                                  />
                                </Box>
                           )}
                        </div>
                        <div
                          className="max-h-[245px] overflow-y-auto bg-[#F4F6F8] p-2 pr-3 mt-2 rounded"
                          style={{ scrollbarGutter: "stable" }}
                        >
                          {Object.values(
                            (rowsData || []).filter(row => row != null).reduce((acc, row) => {
                              const propertyIds = Array.isArray(row?.propertyIds)
                                ? row.propertyIds[0] || "NA"
                                : row?.propertyId || "NA";

                              const propertyName = propertyIds !== "NA"
                                ? dataService.getPropertyById(propertyIds)?.name || "NA"
                                : "NA";

                              if (!acc[propertyIds]) {
                                acc[propertyIds] = { propertyName, items: [] };
                              }

                              acc[propertyIds].items.push(row);
                              return acc;
                            }, {})


                          ).map((group, index) => (
                  <Box key={index} 
                className="">
                  <Typography
                    sx={{
                      color: "#212B36",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "4px"
                    }}
                    >
                    {group.propertyName}
                    {/* Displaying the ID as the main title */}
                  </Typography>

                    <Box
                      sx={{
                        paddingLeft: 2,
                        fontSize: "12px",
                        lineHeight: "18px",
                        fontWeight: 400,
                        marginBottom: "8px"
                      }}
                    >
                      <ul className="space-y-2" style={{ listStyleType: "disc", marginLeft: "20px" }}>
                        {group.items.map((row, rowIndex) => {
                          const isSelectedPlanValid =
                            selectedPlan != null &&
                            selectedPlan !== "" &&
                            row.ccmsTemplateId === selectedPlan.ccmsTemplateId;

                          const currentRowCoaVersion = updatedCoaVersions[row.id] || row.coaVersionId;
                          const baseVersion = currentRowCoaVersion;
                          const targetVersion = selectedPlan?.coaVersion;
                          const baseNum = Number(baseVersion);
                          const targetNum = Number(targetVersion);

                          let message = "";
                          if (
                            !copyDriversOnly &&
                            isSelectedPlanValid &&
                            selectedPlan?.coaVersion &&
                            currentRowCoaVersion &&
                            selectedPlan?.coaVersion !== currentRowCoaVersion
                          ) {
                            if (targetNum < baseNum) {
                              message = `Plan '${row.versionName}' COA version is ahead of '${selectedPlan.versionName}'. Sync not applicable.`;
                            } else if (targetNum > baseNum) {
                              message = `Plans COA versions are not aligned. Please proceed with alignment.`;
                            }
                          }

                          return (
                            <li key={rowIndex} className="!flex !flex-col !items-start">
                              <div className="flex items-center">
                                {row.planTypeName || row.planType || ""} | {row.fiscalYear} | {row.versionName} |{" "} {`V${row?.versionNumber}`}

                                {mode === "plan" && !copyDriversOnly && selectedPlan != null && selectedPlan !== "" && (
                                  row.ccmsTemplateId === selectedPlan.ccmsTemplateId ? null : (
                                    <>
                                      <Block
                                        className="ms-2 flex justify-center items-center"
                                        style={{ width: "14px", height: "14px", color: "#FF5630" }}
                                      />
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          color: "#FF5630",
                                          marginLeft: "4px",
                                        }}
                                      >
                                        Incompatible target plan
                                      </span>
                                    </>
                                  )
                                )}
                              </div>

                              {mode === "plan" && message && (
                                <div className="flex items-start">
                                  <div className="flex items-center flex-1">
                                    <Block
                                      className="flex justify-center items-center "
                                      style={{ width: "14px", height: "14px", color: "#FFAB00" }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        color: "#FFAB00",
                                        marginLeft: "4px",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {message}
                                    </span>
                                  </div>

                                  {targetNum > baseNum && (
                                    <button
                                      disabled={isHmgReprocessing}
                                      className="flex items-center text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                                      onClick={() =>
                                        alignCOAVersion({
                                          organizationId: organizationId,
                                          templateId: row.ccmsTemplateId,
                                          row,
                                          selectedPlan,
                                        })
                                      }
                                    >
                                      <IoMdSync />
                                      <span style={{ fontSize: "10px" }}>Sync</span>
                                    </button>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </Box>
                  </Box>
                ))}
              </div>
            </Box>
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1000,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingScreen title="Aligning coa versions..." position="relative" />
              </Box>
            )}
            <div className="mt-4 pt-4 border-t border-t-[#919EAB33]">
              <Typography
                sx={{ color: "#212A36", fontSize: "14px", fontWeight: 600 }}>
                Copy from
              </Typography>

        {mode === "plan" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {/* Property Selection */}
        <Box>
          <Autocomplete
            popupIcon={<KeyboardArrowDownIcon />}
            size="small"
            options={propertyOptions}
            value={
              propertyOptions.find((o) => o.value === formik.values.propertyId) || null
            }
            getOptionLabel={(option) => option?.label ?? ""}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            noOptionsText="No properties available"
            onChange={(event, newValue) => {
              const value = newValue?.value ?? "";
              formik.setFieldValue("propertyId", value || null);
              setSelectedProperty(value);
              fetchVersions(value, selectedPlanType, selectedFiscalYear);
              formik.setTouched({ ...formik.touched, propertyId: false }, false);
              formik.setErrors({ ...formik.errors, propertyId: undefined });
              setTimeout(() => formik.validateForm(), 0);
            }}
            onBlur={() => formik.setFieldTouched("propertyId", true)}
            slotProps={autocompletePopperSlotProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property*"
                variant="outlined"
                error={formik.touched.propertyId && Boolean(formik.errors.propertyId)}
                sx={autocompleteInputSx}
              />
            )}
          />
          {formik.touched.propertyId && formik.errors.propertyId ? (
            <Box sx={{ display: "flex", alignItems: "center", color: "red" }}>
              <ErrorIcon sx={{ fontSize: 16, mr: 1 }} />
              <Typography variant="body2">This field is required</Typography>
            </Box>
          ) : null}
        </Box>

        {/* Plan Type + Fiscal Year */}
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
        <Box className="!w-full">
          <Autocomplete
            popupIcon={<KeyboardArrowDownIcon />}
            size="small"
            options={planTypeOptions}
            value={planTypeOptions.find((o) => o.value === selectedPlanType) || null}
            getOptionLabel={(option) => option?.label ?? ""}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            onChange={(event, newValue) => {
              const value = newValue?.value ?? "";
              setSelectedPlanType(value);
              fetchVersions(selectedProperty, value, selectedFiscalYear);
            }}
            slotProps={autocompletePopperSlotProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Plan type"
                variant="outlined"
                sx={autocompleteInputSx}
              />
            )}
          />
        </Box>

        <Box className="!w-full">
          <Autocomplete
            popupIcon={<KeyboardArrowDownIcon />}
            variant="outlined"
            size="small"
            options={Array.from({ length: 9 }, (_, i) => dayjs().year() - 3 + i)} // Generates 9 years (currentYear - 3 to currentYear + 5)
            getOptionLabel={(option) => option.toString()}
            value={selectedFiscalYear || null}
            onChange={(event, newValue) => {
              setSelectedFiscalYear(newValue);
              fetchVersions(selectedProperty, selectedPlanType, newValue);
            }}
            slotProps={autocompletePopperSlotProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Fiscal year"
                variant="outlined"
                sx={autocompleteInputSx}
              />
            )}
          />
        </Box>
        </Box>
        {formik.values.propertyId && (
          <>
            {/* Plan Selection */}
            <Box>
              <Autocomplete
                popupIcon={<KeyboardArrowDownIcon />}
                size="small"
                loading={isLoadingPlans}
                options={sortedPlanOptions}
                value={formik.values.selectedPlan || null}
                getOptionLabel={(option) => option?.versionName ?? ""}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                noOptionsText="No plan available"
                onChange={(event, newValue) => {
                  formik.setFieldValue("selectedPlan", newValue || null);
                  setSelectedPlan(newValue);
                  formik.setTouched({ ...formik.touched, selectedPlan: false }, false);
                  formik.setErrors({ ...formik.errors, selectedPlan: undefined });
                  setTimeout(() => formik.validateForm(), 0);
                }}
                onBlur={() => formik.setFieldTouched("selectedPlan", true)}
                slotProps={autocompletePopperSlotProps}
                renderOption={(props, plan) => (
                  <li {...props} key={plan.id} style={{ fontSize: "12px" }}>
                    <PlanOptionLabel plan={plan} />
                  </li>
                )}
                renderInput={(params) => {
                  const plan = formik.values.selectedPlan;
                  return (
                    <TextField
                      {...params}
                      label="Plan*"
                      variant="outlined"
                      error={
                        formik.touched.selectedPlan &&
                        Boolean(formik.errors.selectedPlan)
                      }
                      sx={{
                        ...autocompleteInputSx,
                        ...(plan
                          ? {
                              "& .MuiAutocomplete-input": {
                                opacity: 0,
                                width: 0,
                                minWidth: 0,
                                padding: 0,
                              },
                            }
                          : {}),
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: plan ? (
                          <>
                            <Box sx={{ ml: 0.5 }}>
                              <PlanOptionLabel plan={plan} />
                            </Box>
                            {params.InputProps.startAdornment}
                          </>
                        ) : (
                          params.InputProps.startAdornment
                        ),
                        endAdornment: (
                          <>
                            {isLoadingPlans ? (
                              <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  );
                }}
              />
              {formik.touched.selectedPlan && formik.errors.selectedPlan ? (
                <Box sx={{ display: "flex", alignItems: "center", color: "red" }}>
                  <ErrorIcon sx={{ fontSize: 16, mr: 1 }} />
                  <Typography variant="body2">This field is required</Typography>
                </Box>
              ) : null}
            </Box>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              {/* TPX-2219: "Drivers only" sits to the LEFT of "Copy for all line items". Selecting it copies each
                  source line's driver at rate 0 (no values, no multi-driver actuals residue) as a whole-plan copy. */}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{ ml: "1px" }}
                    checked={copyDriversOnly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCopyDriversOnly(checked);
                      if (checked) {
                        // TPX-2219: drivers-only is a whole-plan copy — force "Copy for all line items",
                        // clear the department/month scope, and turn off the contradictory "Copy only values".
                        setCopyOnlyValue(false);
                        setSelectedGlCodes(glCodeOptions);
                        setSelectedDepartmentNames([]);
                        setSelectedMonths([]);
                      } else {
                        // TPX-2219: deselecting "Copy Driver Only" also clears "Copy for all line items".
                        setSelectedGlCodes([]);
                        setSelectedDepartmentNames([]);
                      }
                    }}
                  />
                }
                label="Copy Driver Only"
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  ".MuiFormControlLabel-label": {
                    fontSize: "14px !important",
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{ ml: "1px" }}
                    // TPX-2219: drivers-only forces a whole-plan copy, so "Copy for all line items" is locked on.
                    disabled={glCodeOptions.length === 0 || copyDriversOnly}
                    checked={isCopyAllLineItems()}
                    onChange={(e) => {
                      setSelectedGlCodes(e.target.checked ? glCodeOptions : []);
                      if (!e.target.checked) setSelectedDepartmentNames([]);
                    }}
                  />
                }
                label="Copy for all line items"
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  ".MuiFormControlLabel-label": {
                    fontSize: "14px !important",
                  },
                }}
              />
              </Box>
            <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            <Box className="w-full">
               <MultiSelectDropdown
                  label="Department(s)"
                  placeholder=""
                  options={[
                    { id: 'select-all', departmentName: 'Select All' },
                    ...[...departmentOptions].sort((a, b) =>
                      departmentRowDisplayName(a).localeCompare(
                        departmentRowDisplayName(b),
                        undefined,
                        { sensitivity: 'base' }
                      )
                    ),
                  ]}
                  value={selectedDepartmentNames}
                  getOptionLabel={(option) =>
                    option.id === 'select-all'
                      ? option.departmentName
                      : departmentRowDisplayName(option)
                  }
                  getOptionId={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isCopyAllLineItems() || copyDriversOnly}
                  onChange={(event, clickedOption) => {
                    const isSelectAllChosen = clickedOption.id === 'select-all';
                    const allSelectable = departmentOptions;

                    if (isSelectAllChosen) {
                      if (selectedDepartmentNames.length === allSelectable.length) {
                        setSelectedDepartmentNames([]);
                      } else {
                        setSelectedDepartmentNames(allSelectable);
                      }
                    } else {
                      const isSelected = selectedDepartmentNames.some(dept => dept.id === clickedOption.id);
                      if (isSelected) {
                        setSelectedDepartmentNames(selectedDepartmentNames.filter(dept => dept.id !== clickedOption.id));
                      } else {
                        setSelectedDepartmentNames([...selectedDepartmentNames, clickedOption]);
                      }
                    }
                  }}
                  searchPlaceholder="Search departments..."
                />
              </Box>
            {/* GL Code (functional code) Selection Dropdown — replaces structure-code "Account(s)". */}
            <Box className="w-full">
              <MultiSelectDropdown
                label="GL code(s)"
                placeholder=""
                options={[
                  { id: 'select-all', displayName: 'Select All' },
                  ...[...glCodeOptions].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
                ]}
                value={selectedGlCodes}
                getOptionLabel={(option) => option.displayName}
                getOptionId={(option) => option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={isCopyAllLineItems() || copyDriversOnly}
                onChange={(event, clickedOption) => {
                  const isSelectAllChosen = clickedOption.id === 'select-all';
                  const allSelectable = glCodeOptions;

                  if (isSelectAllChosen) {
                    if (selectedGlCodes.length === allSelectable.length) {
                      setSelectedGlCodes([]);
                    } else {
                      setSelectedGlCodes(allSelectable);
                    }
                  } else {
                    const isSelected = selectedGlCodes.some((g) => g.id === clickedOption.id);
                    if (isSelected) {
                      setSelectedGlCodes(selectedGlCodes.filter((g) => g.id !== clickedOption.id));
                    } else {
                      setSelectedGlCodes([...selectedGlCodes, clickedOption]);
                    }
                  }
                }}
                searchPlaceholder="Search GL codes..."
              />
            </Box>
            </Box>
            {/* Month-by-month copy: leave empty to copy every month. */}
            <Box sx={{ width: "100%" }}>
              {renderMonthSelector("Month(s)", copyDriversOnly)}
            </Box>
              {/*
                Temporarily hidden: "Copy only values" corrupts DAILY paired-driver lines — the
                copy stamps the line-level driverConfig (whose aggregatedDailyValuesMap only holds
                the first month's day split) onto every month's monthly row instead of rebuilding
                each month's daily map from its own daily rows. The copyOnlyValue state stays at its
                default (false) so the request always sends copyOnlyValue: false. Restore this
                checkbox once the backend DAILY calculate/copy path rebuilds the per-month daily map.
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{ ml: "1px" }}
                    checked={copyOnlyValue}
                    // TPX-2219: disabled while drivers-only is active (contradictory intents).
                    disabled={copyDriversOnly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCopyOnlyValue(checked);
                      // TPX-2219: "Copy only values" and "Drivers only" are contradictory — enabling one clears the other.
                      if (checked) setCopyDriversOnly(false);
                    }}
                  />
                }
                label="Copy only values"
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  ".MuiFormControlLabel-label":{
                                  fontSize: "14px !important",
                                }
                }}
              />
              */}
            </>
        )}
        </Box>
        )}

        {mode === "actuals" && (
          <Box className="mt-4">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <RadioGroup
                row
                value={copyBasis}
                onChange={(e) => setCopyBasis(e.target.value)}
                sx={{ gap: "24px" }}
              >
                <FormControlLabel
                  value="YEAR"
                  control={<Radio size="small" sx={{ p: 0.5 }} />}
                  sx={{ alignItems: "center", margin: 0 }}
                  label={
                    <Typography sx={{ fontFamily: "Public Sans", fontWeight: 400, fontSize: "14px", lineHeight: "22px", color: "#212B36" }}>
                      Based on year
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="CLOSED_MONTH"
                  control={<Radio size="small" sx={{ p: 0.5 }} />}
                  sx={{ alignItems: "center", margin: 0 }}
                  label={
                    <Typography sx={{ fontFamily: "Public Sans", fontWeight: 400, fontSize: "14px", lineHeight: "22px", color: "#212B36" }}>
                      Based on closed month
                    </Typography>
                  }
                />
              </RadioGroup>
              {copyBasis === "YEAR" ? (
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Year"
                      views={["year"]}
                      disableClearable
                      value={dayjs().year(actualsCopyYear).startOf("year")}
                      onChange={(newValue) => {
                        if (newValue && newValue.isValid()) {
                          setActualsCopyYear(newValue.year());
                        } else {
                          setActualsCopyYear(dayjs().year());
                        }
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          required: true,
                          variant: "outlined",
                          sx: {
                            ".MuiInputLabel-root": { fontSize: "14px" },
                            ".MuiOutlinedInput-root": { fontSize: "14px" },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {renderMonthSelector("Month(s)*")}
                </Box>
              </Box>
              ) : (
              <Box sx={{ width: "100%" }}>
                <Typography sx={{ fontFamily: "Public Sans", fontWeight: 600, fontSize: "12px", lineHeight: "18px", color: "#637381", mb: 0.5 }}>
                  Closed month
                </Typography>
                {closedMonthLabel ? (
                  <Typography sx={{ fontFamily: "Public Sans", fontWeight: 400, fontSize: "14px", lineHeight: "22px", color: "#212B36" }}>
                    {closedMonthLabel}
                  </Typography>
                ) : (
                  <Typography sx={{ fontFamily: "Public Sans", fontWeight: 400, fontSize: "14px", lineHeight: "22px", color: "#B71D18" }}>
                    No closed month is configured. Configure it in settings before copying by closed month.
                  </Typography>
                )}
              </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{ ml: "1px" }}
                    disabled={glCodeOptions.length === 0}
                    checked={isCopyAllLineItems()}
                    onChange={(e) => {
                      setSelectedGlCodes(e.target.checked ? glCodeOptions : []);
                    }}
                  />
                }
                label="Copy actuals for entire plan"
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  ".MuiFormControlLabel-label": {
                    fontSize: "14px !important",
                  },
                }}
              />
              <Box sx={{ width: "100%" }}>
                <MultiSelectDropdown
                  label="GL code(s)*"
                  placeholder=""
                  options={[
                    // { id: "select-all", displayName: "Select All" },
                    ...[...glCodeOptions].sort((a, b) =>
                      (a.displayName || "").localeCompare(b.displayName || "")
                    ),
                  ]}
                  value={selectedGlCodes}
                  getOptionLabel={(option) => option.displayName}
                  getOptionId={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isCopyAllLineItems()}
                  onChange={(event, clickedOption) => {
                    const isSelectAll = clickedOption.id === "select-all";
                    if (isSelectAll) {
                      if (selectedGlCodes.length === glCodeOptions.length) {
                        setSelectedGlCodes([]);
                      } else {
                        setSelectedGlCodes(glCodeOptions);
                      }
                    } else {
                      const isSelected = selectedGlCodes.some((g) => g.id === clickedOption.id);
                      if (isSelected) {
                        setSelectedGlCodes(
                          selectedGlCodes.filter((g) => g.id !== clickedOption.id)
                        );
                      } else {
                        setSelectedGlCodes([...selectedGlCodes, clickedOption]);
                      }
                    }
                  }}
                  searchPlaceholder="Search GL codes..."
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2, borderColor: "#919EAB33" }} />

            <RadioGroup
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              sx={{ gap: "12px" }}
            >
              <FormControlLabel
                value="LIVE"
                disabled={!liveIntegrationEnabled}
                control={<Radio size="small" sx={{ p: 0.5 }} />}
                sx={{ alignItems: "center", margin: 0 }}
                // TODO: restore sync details label (dot + "Last synced" text) once lastSyncedAt is
                // available from backend. Re-enable: lastSyncedAt state, setLastSyncedAt call,
                // and getSyncDotColor helper above, then replace label below with the Box/conditional.
                label={
                  <Typography
                    sx={{
                      fontFamily: "Public Sans",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "22px",
                      color: liveIntegrationEnabled ? "#212B36" : "#919EAB",
                    }}
                  >
                    Live integration
                  </Typography>
                }
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 1,
                  width: "100%",
                  m: 0,
                }}
              >
                <FormControlLabel
                  value="TEMPLATE"
                  control={<Radio size="small" sx={{ p: 0.5 }} />}
                  sx={{ alignItems: "center", margin: 0 }}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        maxWidth: "100%",
                        rowGap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Public Sans",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "22px",
                          color: "#212B36",
                        }}
                      >
                        Manual uploaded
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "Public Sans",
                          fontSize: "13px",
                          lineHeight: "22px",
                          color: "#637381",
                          maxWidth: 480,
                        }}
                      >
                        To upload the latest actuals,{" "}
                        <Link
                          component="button"
                          type="button"
                          disabled={selectedSource === "LIVE"}
                          onClick={() => {
                            if (selectedSource === "LIVE") return;
                            setTemplateUploadOpen(true);
                            setCurrentModalName("Upload/download actuals");
                          }}
                          sx={{
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            lineHeight: "inherit",
                            fontWeight: 600,
                            color: selectedSource === "LIVE" ? "#919EAB" : "#2A5BA2",
                            cursor: selectedSource === "LIVE" ? "default" : "pointer",
                            textDecoration: selectedSource === "LIVE" ? "none" : "underline",
                            verticalAlign: "baseline",
                            border: "none",
                            background: "none",
                            padding: 0,
                            pointerEvents: selectedSource === "LIVE" ? "none" : "auto",
                            "&:hover": {
                              color: selectedSource === "LIVE" ? "#919EAB" : "#1e4580",
                            },
                          }}
                        >
                          click here
                        </Link>
                        .
                      </Typography>
                      {(() => {
                        const d =
                          manualActualsLastUploadedAt != null
                            ? dayjs(manualActualsLastUploadedAt)
                            : null;
                        const hasValidDate = d != null && d.isValid();
                        const showMetaRow = hasValidDate || manualActualsLastUploadedBy != null;
                        if (!showMetaRow) return null;
                        return (
                          <>
                            {hasValidDate && (
                              <Typography
                                sx={{
                                  fontFamily: "Public Sans",
                                  fontWeight: 400,
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  color: "#637381",
                                }}
                              >
                                {`Last uploaded: ${d.format("MMM D, YYYY h:mm A")}`}
                              </Typography>
                            )}
                            <Tooltip
                              title={
                                manualActualsLastUploadedBy
                                  ? `Uploaded by: ${manualActualsLastUploadedBy}`
                                  : "Uploader name was not returned for this upload."
                              }
                              arrow
                              placement="top"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 18,
                                  color: "#637381",
                                  cursor: "help",
                                  verticalAlign: "middle",
                                }}
                                aria-label="Manual Actuals upload details"
                              />
                            </Tooltip>
                          </>
                        );
                      })()}
                    </Box>
                  }
                />
                {selectedSource === "TEMPLATE" && templateFile ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      maxWidth: "100%",
                      pl: "30px",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        width: 18,
                        height: 18,
                        color: "#118D57",
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Public Sans",
                        fontSize: "13px",
                        lineHeight: "20px",
                        color: "#212B36",
                        minWidth: 0,
                        wordBreak: "break-word",
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, color: "#118D57" }}>
                        Actuals uploaded successfully
                      </Box>
                      <Box component="span" sx={{ fontWeight: 400 }}>
                        {" "}
                        {templateFile.name}
                      </Box>
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </RadioGroup>
          </Box>
        )}
        </div>
        </>
         }
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 0,
          mt: 2,
        }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isImporting || validatingCopy}
          className="px-3 py-1.5 text-sm !font-bold"
          sx={{ color: "#212B36", borderColor: "#919EAB52" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error" 
          disabled={
            isImporting ||
            validatingCopy ||
            isHmgReprocessing ||
            // TPX-2219: drivers-only is allowed across COA versions, so a ccmsTemplateId mismatch does not block it.
            (mode === "plan" && !copyDriversOnly && areAllPlansIncompatible()) ||
            (mode === "actuals" && !isActualsFormValid())
          }
          className="!text-sm !leading-[24px] !font-bold px-3 !py-[7px] !rounded-lg"
          onClick={handleImportClick}
          sx={{ bgcolor: "#212B36" }}>
          {validatingCopy ? "Checking…" : (mode === "plan" && copyDriversOnly ? "Copy Driver Only" : "Copy data")}
        </Button>
        <ImportPlanCopyValidationModal
          open={copyValidationOpen}
          onClose={handleCopyValidationClose}
          onContinue={handleCopyValidationContinue}
          validationResult={copyValidationResult}
          validating={validatingCopy}
          copyAllMode={mode === "plan" && isCopyAllLineItems()}
        />
        {openInitializePlanConfirmationModal && (
        <InitializePlanConfirmationModal
          isOpen={openInitializePlanConfirmationModal}
          onClose={() => handelinitializePlanModalClose()}
          onSubmit={() => handleImport()}
          mode={mode}
          copyDriversOnly={copyDriversOnly}
        />
      )}
      </DialogActions>
      {templateUploadOpen && (
        <UploadDownloadActualsDialog
          open={templateUploadOpen}
          onClose={() => {
            setTemplateUploadOpen(false);
            setCurrentModalName(mode === "actuals" ? "Copy data from actuals" : "Copy data from plan");
          }}
          showToast={showToast}
          disableUploadOption={false}
          onUploadComplete={(file) => {
            if (file) setTemplateFile(file);
            setCurrentModalName(mode === "actuals" ? "Copy data from actuals" : "Copy data from plan");
            if (mode === "actuals" && organizationId && file) {
              dataService.getClosedMonthAccountData(organizationId).then((data) => {
                if (data != null) {
                  const manualMeta = pickManualActualsUploadMeta(data);
                  setManualActualsLastUploadedAt(manualMeta.uploadedAt);
                  setManualActualsLastUploadedBy(manualMeta.uploadedBy);
                }
              });
            }
          }}
        />
      )}
    </Dialog>
  );
};

export default ImportActualModal;